import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChartCard from "@/components/data-visuals/ChartCard";
import AdminDocLinkStatCard from "@/components/admin/AdminDocLinkStatCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import {
  EventAvailable,
  CalendarMonth,
  Timer,
  Search,
  MedicalServicesOutlined,
  EmojiEvents,
} from "@mui/icons-material";
import DocLinkLeaderboardDrawer from "@/components/admin/doclink/DocLinkLeaderboardDrawer";
import LeaderboardManageModal from "@/components/admin/doclink/LeaderboardManageModal";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import {
  ADMIN_TOP_CARD_HEIGHT,
  adminDateRangePickerWideNestedSx,
  docLinkPurpleDataGridSx,
} from "@/utils/adminTableStyles";
import { getDocLinkProviderAppointmentCount } from "@/utils/adminDashboardUtils";

const PROVIDER_FALLBACK = {
  Medmate: "#2563eb",
  Hubmed: "#ec4899",
  Hola: "#22c55e",
};

const mapProviderPie = (providerPie) => {
  if (!Array.isArray(providerPie)) return [];
  return providerPie
    .map((p) => ({
      x: p.provider || p.Provider,
      y: Number(p.count ?? 0),
      color: p.color || PROVIDER_FALLBACK[p.provider || p.Provider] || "#888",
    }))
    .filter((d) => d.y > 0 && d.x);
};

const preprocessDocLinkBookings = (data, dateStart, dateEnd) => {
  const startDate = new Date(dateStart);
  const endDate = new Date(dateEnd);
  const dateMap = new Map();

  (data || []).forEach((item) => {
    if (!item?.BookingDate) return;
    const dateKey = new Date(item.BookingDate).toISOString().split("T")[0];

    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, {
        BookingDate: dateKey,
        TotalScriptRequests: 0,
        TotalConsults: 0,
        TotalBookings: 0,
      });
    }

    const existingData = dateMap.get(dateKey);
    existingData.TotalScriptRequests +=
      parseInt(item.TotalScriptRequests, 10) || 0;
    existingData.TotalConsults += parseInt(item.TotalConsults, 10) || 0;
    existingData.TotalBookings += parseInt(item.TotalBookings, 10) || 0;
    dateMap.set(dateKey, existingData);
  });

  const result = [];
  for (
    let date = new Date(startDate);
    date <= endDate;
    date.setDate(date.getDate() + 1)
  ) {
    const dateKey = date.toISOString().split("T")[0];
    result.push(
      dateMap.get(dateKey) || {
        BookingDate: dateKey,
        TotalScriptRequests: 0,
        TotalConsults: 0,
        TotalBookings: 0,
      }
    );
  }

  return result;
};

const fmtCount = (n) =>
  n != null && Number.isFinite(n) ? Number(n).toLocaleString() : "—";

const apptColumns = [
  {
    field: "ChemistName",
    headerName: "Chemist",
    flex: 1,
    minWidth: 160,
    renderCell: (p) => getChemistName(p.value),
  },
  { field: "FirstName", headerName: "First name", width: 120 },
  { field: "LastName", headerName: "Last name", width: 120 },
  { field: "Provider", headerName: "Provider", width: 110 },
  { field: "ProviderOrderID", headerName: "Order ID", width: 130 },
  {
    field: "CreateDate",
    headerName: "Create date",
    width: 120,
    type: "date",
    valueGetter: (_v, row) => row.CreateDate ? new Date(row.CreateDate) : null,
    renderCell: (p) => p.row.CreateDate ?? "—",
  },
  { field: "Status", headerName: "Status", width: 160 },
  {
    field: "WaitMinutes",
    headerName: "Wait time",
    width: 110,
    type: "number",
    renderCell: (p) =>
      p.row.WaitMinutes != null ? `${p.row.WaitMinutes} min` : "—",
  },
];

const AdminDocLinkPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const [data, setData] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [selectedProviderPieItem, setSelectedProviderPieItem] = useState({ seriesId: '__none__', dataIndex: -1 });
  const [fetchError, setFetchError] = useState(null);
  const [timeChartOpen, setTimeChartOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const [apptData, setApptData] = useState(null);
  const [apptLoading, setApptLoading] = useState(false);
  const [apptError, setApptError] = useState(null);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [manageCompOpen, setManageCompOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    if (!dateStart || !dateEnd) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const params = { DateStart: dateStart, DateEnd: dateEnd };
        const response = await phappsRequest(
          isManager ? API_PATHS.MANAGER_FUNCTIONS_DOCLINK : API_PATHS.ADMIN_FUNCTIONS_DOCLINK,
          "GET",
          null,
          params
        );
        if (!cancelled) {
          setData(response);
          setFetchError(null);
        }
      } catch (err) {
        console.error("Error fetching admin DocLink data:", err);
        if (!cancelled) {
          setData(null);
          setFetchError(
            getAdminApiErrorMessage(err, "Failed to load DocLink data")
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateStart, dateEnd]);

  const openApptDialog = async () => {
    setApptOpen(true);
    if (apptData) return;
    try {
      setApptLoading(true);
      setApptError(null);
      const res = await phappsRequest(
        API_PATHS.ADMIN_FUNCTIONS_DOCLINK_APPOINTMENTS,
        "GET",
        null,
        { DateStart: dateStart, DateEnd: dateEnd }
      );
      setApptData((res?.Appointments ?? []).map((r, i) => ({ ...r, id: r.ConsultID ?? `a-${i}` })));
    } catch (err) {
      setApptError(getAdminApiErrorMessage(err, "Failed to load appointments"));
    } finally {
      setApptLoading(false);
    }
  };

  const handleProviderPieClick = useCallback((event, identifier, item) => {
    const isSame = selectedProviderPieItem?.dataIndex === identifier.dataIndex
                   && selectedProviderPieItem?.dataIndex !== -1;
    if (isSame) {
      setSelectedProviderPieItem({ seriesId: '__none__', dataIndex: -1 });
      setProviderFilter("all");
    } else {
      setSelectedProviderPieItem({ seriesId: identifier.seriesId, dataIndex: identifier.dataIndex });
      setProviderFilter(item.label || "all");
    }
  }, [selectedProviderPieItem]);

  const summary = data?.Summary || {};
  const overviewRows = data?.OverviewTable || [];
  const pieProvider = useMemo(
    () => mapProviderPie(data?.ProviderPie),
    [data]
  );

  const pieConsultType = useMemo(() => {
    const raw = data?.ConsultTypePie;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((r) => Number(r.count ?? 0) > 0)
      .map((r) => ({ x: r.name, y: Number(r.count), color: r.color }));
  }, [data]);

  const rawBookings =
    data?.TotalBookingsPerDay || data?.AppointmentsPerDay || [];
  const bookingsFieldMissing =
    data &&
    !Object.prototype.hasOwnProperty.call(data, "TotalBookingsPerDay") &&
    !Object.prototype.hasOwnProperty.call(data, "AppointmentsPerDay");

  const processedBookings = useMemo(
    () => preprocessDocLinkBookings(rawBookings, dateStart, dateEnd),
    [rawBookings, dateStart, dateEnd]
  );

  const chartData = useMemo(
    () => ({ TotalBookingsPerDay: processedBookings }),
    [processedBookings]
  );

  const totalAppointments = useMemo(
    () =>
      overviewRows.reduce(
        (s, r) => s + (parseInt(r.TotalAppointments, 10) || 0),
        0
      ),
    [overviewRows]
  );

  const avgWaitMedmateDisplay = summary.avgWaitMedmateMinutes != null
    ? Number(summary.avgWaitMedmateMinutes).toFixed(1)
    : "—";
  const avgWaitHolaDisplay = summary.avgWaitHolaMinutes != null
    ? Number(summary.avgWaitHolaMinutes).toFixed(1)
    : "—";
  const avgApptPerWeekDisplay = Number(
    summary.averageAppointmentsPerWeek ?? 0
  ).toFixed(2);

  const tableRows = overviewRows.map((row, i) => ({
    id: `${row.ChemistUUID || "noid"}-${i}`,
    chemistName: row.ChemistName,
    TotalAppointments: row.TotalAppointments ?? 0,
    medmate: getDocLinkProviderAppointmentCount(row, "Medmate"),
    hubmed: getDocLinkProviderAppointmentCount(row, "Hubmed"),
    hola: getDocLinkProviderAppointmentCount(row, "Hola"),
    defaultProvider: row.DefaultDocLinkProvider ?? "—",
    ChemistUUID: row.ChemistUUID,
  }));

  const filteredTableRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    return tableRows.filter((r) => {
      if ((r.TotalAppointments ?? 0) === 0) return false;
      if (q && !String(getChemistName(r.chemistName) || "").toLowerCase().includes(q)) return false;
      if (providerFilter !== "all") {
        const dp = String(r.defaultProvider ?? "").toLowerCase();
        if (dp !== providerFilter.toLowerCase()) return false;
      }
      return true;
    });
  }, [tableRows, nameFilter, providerFilter]);

  const needsPerChemistProviderCounts = useMemo(
    () =>
      tableRows.some(
        (r) => r.medmate == null && r.hubmed == null && r.hola == null
      ),
    [tableRows]
  );

  const tableColumns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Typography
          variant="body2"
          onClick={() => p.row.ChemistUUID && navigate(`${chemistBasePath}/${encodeURIComponent(p.row.ChemistUUID)}`, { state: { tab: 4 } })}
          sx={{ cursor: p.row.ChemistUUID ? "pointer" : "default", fontWeight: 500, "&:hover": { textDecoration: p.row.ChemistUUID ? "underline" : "none" } }}
        >
          {getChemistName(p.value)}
        </Typography>
      ),
    },
    {
      field: "medmate",
      headerName: "Medmate",
      width: 110,
      type: "number",
      headerAlign: "center",
      align: "center",
      valueGetter: (_v, row) => row.medmate ?? -1,
      renderCell: (p) => fmtCount(p.row.medmate),
    },
    {
      field: "hubmed",
      headerName: "Hubmed",
      width: 110,
      type: "number",
      headerAlign: "center",
      align: "center",
      valueGetter: (_v, row) => row.hubmed ?? -1,
      renderCell: (p) => fmtCount(p.row.hubmed),
    },
    {
      field: "hola",
      headerName: "Hola",
      width: 100,
      type: "number",
      headerAlign: "center",
      align: "center",
      valueGetter: (_v, row) => row.hola ?? -1,
      renderCell: (p) => fmtCount(p.row.hola),
    },
    {
      field: "TotalAppointments",
      headerName: "Total appointments",
      width: 150,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "defaultProvider",
      headerName: "Default DocLink provider",
      width: 190,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "actions",
      headerName: "Open",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          onClick={() =>
            p.row.ChemistUUID &&
            navigate(
              `${chemistBasePath}/${encodeURIComponent(p.row.ChemistUUID)}`,
              { state: { tab: 4 } }
            )
          }
          disabled={!p.row.ChemistUUID}
          sx={{
            textTransform: "none",
            backgroundColor: DOCLINK_COLORS.MID_PURPLE,
            minWidth: 68,
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  const docLinkDateRangeSx = {
    borderRadius: "8px",
    p: 2,
    height: ADMIN_TOP_CARD_HEIGHT,
    minHeight: ADMIN_TOP_CARD_HEIGHT,
    maxHeight: ADMIN_TOP_CARD_HEIGHT,
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    backgroundColor: DOCLINK_COLORS.DARK_PURPLE,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    "& .text-black": { color: "white !important" },
    ...adminDateRangePickerWideNestedSx,
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        {isLoading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading DocLink data…</p>
            </div>
          </div>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
              >
                Admin DocLink overview
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EmojiEvents />}
                onClick={() => setLeaderboardOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: DOCLINK_COLORS.MID_PURPLE,
                  color: DOCLINK_COLORS.DARK_PURPLE,
                  "&:hover": {
                    borderColor: DOCLINK_COLORS.DARK_PURPLE,
                    bgcolor: DOCLINK_COLORS.LAVENDER_TINT,
                  },
                }}
              >
                Leaderboard
              </Button>
              {!isManager && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setManageCompOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: DOCLINK_COLORS.MID_PURPLE,
                    color: DOCLINK_COLORS.DARK_PURPLE,
                    "&:hover": {
                      borderColor: DOCLINK_COLORS.DARK_PURPLE,
                      bgcolor: DOCLINK_COLORS.LAVENDER_TINT,
                    },
                  }}
                >
                  Manage competitions
                </Button>
              )}
            </Box>
            {fetchError ? (
              <Alert severity="warning">{fetchError}</Alert>
            ) : null}

            {bookingsFieldMissing ? (
              <Alert severity="info">
                Appointments over time need a daily series from the API (for
                example <code>TotalBookingsPerDay</code> with{" "}
                <code>BookingDate</code>, <code>TotalConsults</code>,{" "}
                <code>TotalScriptRequests</code>) for the selected range.
              </Alert>
            ) : null}

            {needsPerChemistProviderCounts ? (
              <Alert severity="info" sx={{ maxWidth: 900 }}>
                Per-chemist columns <strong>Medmate</strong>,{" "}
                <strong>Hubmed</strong>, and <strong>Hola</strong> need counts on
                each <code>OverviewTable</code> row (for example{" "}
                <code>MedmateAppointments</code>, <code>HubmedAppointments</code>,{" "}
                <code>HolaAppointments</code> or a{" "}
                <code>ProviderAppointmentCounts</code> map). Until provided, those
                cells show &quot;—&quot;.
              </Alert>
            ) : null}

            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <Box sx={docLinkDateRangeSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    setDateStart(range[0]?.toISOString().split("T")[0]);
                    setDateEnd(range[1]?.toISOString().split("T")[0]);
                  }}
                />
              </Box>

              <div className="min-w-0">
                <AdminDocLinkStatCard
                  title="Avg wait — Medmate (min)"
                  value={avgWaitMedmateDisplay}
                  valueFormatter={(v) => v}
                  Icon={Timer}
                />
              </div>
              <div className="min-w-0">
                <AdminDocLinkStatCard
                  title="Avg wait — Hola (min)"
                  value={avgWaitHolaDisplay}
                  valueFormatter={(v) => v}
                  Icon={Timer}
                />
              </div>
              <div className="min-w-0">
                <AdminDocLinkStatCard
                  title="Avg appointments / week"
                  value={avgApptPerWeekDisplay}
                  valueFormatter={(v) => v}
                  Icon={CalendarMonth}
                />
              </div>
              <div className="min-w-0">
                <AdminDocLinkStatCard
                  title="Appointments (range)"
                  value={totalAppointments}
                  valueFormatter={(v) => Number(v || 0).toLocaleString()}
                  Icon={EventAvailable}
                />
              </div>
            </section>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
                height: { lg: "calc(100vh - 290px)" },
                minHeight: { lg: 480 },
                overflow: { lg: "hidden" },
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  height: "100%",
                  borderColor: DOCLINK_COLORS.DARK_PURPLE,
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    "&:last-child": { pb: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      fontWeight={700}
                      sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
                    >
                      Chemists
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setTimeChartOpen(true)}
                        sx={{
                          textTransform: "none",
                          borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          color: DOCLINK_COLORS.DARK_PURPLE,
                          fontWeight: 600,
                        }}
                      >
                        See appointments over time
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={openApptDialog}
                        sx={{
                          textTransform: "none",
                          borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          color: DOCLINK_COLORS.DARK_PURPLE,
                          fontWeight: 600,
                        }}
                      >
                        Appointment overview
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Provider</InputLabel>
                        <Select
                          label="Provider"
                          value={providerFilter}
                          onChange={(e) => {
                            setProviderFilter(e.target.value);
                            setSelectedProviderPieItem({ seriesId: '__none__', dataIndex: -1 });
                          }}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="Medmate">Medmate</MenuItem>
                          <MenuItem value="Hubmed">Hubmed</MenuItem>
                          <MenuItem value="Hola">Hola</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        placeholder="Filter by chemist name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 220 }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      px: 1,
                      pb: 2,
                      overflow: "hidden",
                    }}
                  >
                    <DataGrid
                      rows={filteredTableRows}
                      columns={tableColumns}
                      loading={isLoading}
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                      }}
                      disableRowSelectionOnClick
                      sx={{
                        ...docLinkPurpleDataGridSx,
                        flex: 1,
                        minHeight: 0,
                        height: "100%",
                        width: "100%",
                        "& .MuiDataGrid-virtualScroller": {
                          scrollbarGutter: "stable",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Box
                sx={{
                  width: { xs: "100%", lg: 380 },
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 0,
                  "& > *": {
                    flex: 1,
                    minHeight: 0,
                    marginTop: "0 !important",
                  },
                }}
              >
                <ChartCard
                  chartId="doclink-provider-pie"
                  title="Appointments by provider"
                  hideMainChart
                  fillParentHeight
                  onPieItemClick={handleProviderPieClick}
                  selectedPieItem={selectedProviderPieItem}
                  data={{}}
                  primaryXAxis={{}}
                  primaryYAxis={{}}
                  borderClass="border-4 border-purple-light"
                  series={[]}
                  pieData={
                    pieProvider.length
                      ? pieProvider
                      : [
                          { x: "Medmate", y: 0, color: PROVIDER_FALLBACK.Medmate },
                          { x: "Hubmed", y: 0, color: PROVIDER_FALLBACK.Hubmed },
                          { x: "Hola", y: 0, color: PROVIDER_FALLBACK.Hola },
                        ]
                  }
                  pieTitle="By provider"
                />
                <ChartCard
                  chartId="doclink-consult-type-pie"
                  title="Appointments by consult type"
                  hideMainChart
                  fillParentHeight
                  data={{}}
                  primaryXAxis={{}}
                  primaryYAxis={{}}
                  borderClass="border-4 border-purple-light"
                  series={[]}
                  pieData={pieConsultType.length ? pieConsultType : [{ x: "No data", y: 1, color: DOCLINK_COLORS.LIGHT_PURPLE }]}
                  pieTitle="By consult type"
                />
              </Box>
            </Box>

            <Dialog
              open={apptOpen}
              onClose={() => setApptOpen(false)}
              maxWidth="xl"
              fullWidth
            >
              <DialogTitle sx={{ color: DOCLINK_COLORS.DARK_PURPLE, fontWeight: 700 }}>
                Appointment overview
              </DialogTitle>
              <DialogContent>
                {apptError ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>{apptError}</Alert>
                ) : null}
                <div style={{ height: 520, width: "100%" }}>
                  <DataGrid
                    rows={apptData ?? []}
                    columns={apptColumns}
                    loading={apptLoading}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    disableRowSelectionOnClick
                    sx={docLinkPurpleDataGridSx}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setApptOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={timeChartOpen}
              onClose={() => setTimeChartOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle
                sx={{ color: DOCLINK_COLORS.DARK_PURPLE, fontWeight: 700 }}
              >
                Appointments over time
              </DialogTitle>
              <DialogContent>
                <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                  <ChartCard
                    title=""
                    hidePieChart
                    mainChartMinHeight={360}
                    data={chartData}
                    primaryXAxis={{
                      valueType: "DateTime",
                      title: "Booking Date",
                      labelFormat: "dd/MM",
                      intervalType: "Days",
                    }}
                    primaryYAxis={{
                      title: "Appointments",
                      labelFormat: "{value}",
                    }}
                    borderClass="border-4 border-purple-light"
                    series={[
                      {
                        dataSource: chartData.TotalBookingsPerDay || [],
                        xName: "BookingDate",
                        yName: "TotalConsults",
                        type: "StackingColumn",
                        name: "Consults",
                        fill: "#E35437",
                        columnSpacing: 0.4,
                        cornerRadius: { topLeft: 4, topRight: 4 },
                      },
                      {
                        dataSource: chartData.TotalBookingsPerDay || [],
                        xName: "BookingDate",
                        yName: "TotalScriptRequests",
                        type: "StackingColumn",
                        name: "Script Requests",
                        fill: "#8d679f",
                        columnSpacing: 0.4,
                        cornerRadius: { topLeft: 4, topRight: 4 },
                      },
                    ]}
                    pieData={[]}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTimeChartOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>

      <DocLinkLeaderboardDrawer
        open={leaderboardOpen}
        onClose={() => setLeaderboardOpen(false)}
      />

      <LeaderboardManageModal
        open={manageCompOpen}
        onClose={() => setManageCompOpen(false)}
      />
    </div>
  );
};

export default AdminDocLinkPage;
